<template>
  <Teleport to="body">
    <div
      ref="panelEl"
      v-bind="$attrs"
      class="notification-panel"
      :class="{ 'visibility-hidden': !open }"
      ph-autocapture="notifications"
    >
      <div class="notification-panel__head">
        <div class="notification-panel__title">
          {{ $t('notificationPanel.title') }}
        </div>
        <div v-show="totalCount > 0" class="notification-panel__actions">
          <a
            v-show="unreadCount > 0"
            class="notification-panel__action"
            @click="markAllAsRead"
          >
            {{ $t('notificationPanel.markAllAsRead') }}
          </a>
          <a
            class="notification-panel__action"
            @click="$refs.clearAllConfirmModal.show()"
          >
            {{ $t('notificationPanel.clearAll') }}
          </a>
        </div>
      </div>
      <!-- Origem Flow: ativa as notificações de sistema (Web Push) deste
           dispositivo — some depois de ativado ou se o navegador bloqueou. -->
      <div v-if="showEnablePush" class="notification-panel__push-banner">
        <a class="notification-panel__push-banner-link" @click="enablePush">
          <i class="iconoir-bell-notification"></i>
          {{ $t('notificationPanel.enablePush') }}
        </a>
      </div>
      <div v-if="!loaded && loading" class="loading-absolute-center"></div>
      <div v-else-if="totalCount === 0" class="notification-panel__empty">
        <i class="notification-panel__empty-icon iconoir-bell-off"></i>
        <div class="notification-panel__empty-title">
          {{ $t('notificationPanel.noNotificationTitle') }}
        </div>
        <div class="notification-panel__empty-text">
          {{ $t('notificationPanel.noNotification') }}
        </div>
      </div>
      <div v-else class="notification-panel__body">
        <div v-if="needRefresh" class="notification-panel__refresh-hint">
          <div class="notification-panel__refresh-hint-text">
            <span class="notification-panel__refresh-hint-icon"></span>
            {{ $t('notificationPanel.newNotificationsAvailable') }}
          </div>
          <Button type="secondary" @click.prevent="initialLoad">
            {{ $t('notificationPanel.refresh') }}
          </Button>
        </div>
        <InfiniteScroll
          ref="infiniteScroll"
          :current-count="currentCount"
          :max-count="totalCount"
          :loading="loading"
          :render-end="false"
          @load-next-page="loadNextPage"
        >
          <template #default>
            <!-- Origem Flow: notificações da mesma tarefa pelo mesmo autor
                 em sequência viram um grupo ("+N"); clique expande. -->
            <div
              v-for="group in groupedNotifications"
              :key="group.key"
              class="notification-panel__notification"
              :class="{
                'notification-panel__notification--unread': !group.first.read,
                'notification-panel__notification--group': group.rest.length,
              }"
            >
              <div class="notification-panel__notification-icon">
                <component
                  :is="getNotificationIcon(group.first)"
                  :notification="group.first"
                  v-bind="getNotificationIconProps(group.first)"
                >
                </component>
              </div>
              <div class="notification-panel__notification-content">
                <component
                  :is="getNotificationContent(group.first)"
                  :notification="group.first"
                  :workspace="workspace"
                  @close-panel="hide"
                >
                </component>
                <div class="notification-panel__notification-time">
                  {{ timeAgo(group.first.created_on) }}
                  <a
                    v-if="group.rest.length && !expanded[group.key]"
                    class="notification-panel__group-more"
                    @click.prevent="expanded[group.key] = true"
                  >
                    {{
                      $t('notificationPanel.groupMore', {
                        count: group.rest.length,
                      })
                    }}
                  </a>
                </div>
                <div
                  v-if="group.rest.length && expanded[group.key]"
                  class="notification-panel__group-rest"
                >
                  <div
                    v-for="item in group.rest"
                    :key="item.id"
                    class="notification-panel__group-item"
                    :class="{
                      'notification-panel__group-item--unread': !item.read,
                    }"
                  >
                    <component
                      :is="getNotificationContent(item)"
                      :notification="item"
                      :workspace="workspace"
                      @close-panel="hide"
                    >
                    </component>
                    <div class="notification-panel__notification-time">
                      {{ timeAgo(item.created_on) }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="notification-panel__notification-status">
                <span v-if="group.unread"></span>
              </div>
            </div>
          </template>
        </InfiniteScroll>
      </div>
      <ClearAllNotificationsConfirmModal
        ref="clearAllConfirmModal"
        @confirm="
          ($event) => {
            $event.preventDefault()
            $event.stopPropagation()
            clearAll()
          }
        "
        @cancel="
          ($event) => {
            $event.preventDefault()
            $event.stopPropagation()
          }
        "
      ></ClearAllNotificationsConfirmModal>
    </div>
  </Teleport>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from '@baserow/modules/core/moment'
import {
  collectTeleportRootsForOutsideClick,
  getTeleportedElementFromRef,
  onClickOutside,
} from '@baserow/modules/core/utils/dom'
import { notifyIf } from '@baserow/modules/core/utils/error'
import InfiniteScroll from '@baserow/modules/core/components/helpers/InfiniteScroll'
import ClearAllNotificationsConfirmModal from '@baserow/modules/core/components/modals/ClearAllNotificationsConfirmModal'

export default {
  name: 'NotificationPanel',
  components: {
    ClearAllNotificationsConfirmModal,
    InfiniteScroll,
  },
  provide() {
    return {
      registerChild: this.registerChild,
    }
  },
  inheritAttrs: false,
  emits: ['hidden', 'shown'],
  data() {
    return {
      open: false,
      needRefresh: false,
      removeOnClickOutsideHandler: null,
      children: [],
      showEnablePush: false,
      expanded: {},
    }
  },
  computed: {
    ...mapGetters({
      workspaceId: 'notification/getWorkspaceId',
      notifications: 'notification/getAll',
      loading: 'notification/getLoading',
      loaded: 'notification/getLoaded',
      unreadCount: 'notification/getUnreadCount',
      currentCount: 'notification/getCurrentCount',
      totalCount: 'notification/getTotalCount',
    }),
    workspace() {
      return this.$store.getters['workspace/get'](this.workspaceId)
    },
    /**
     * Origem Flow: agrupa notificações consecutivas do mesmo tipo, da mesma
     * linha e do mesmo autor (ex.: 4 comentários seguidos na mesma tarefa).
     * Só agrupa o que tem linha (row_id); convites e avisos ficam soltos.
     */
    groupedNotifications() {
      const groups = []
      for (const notification of this.notifications || []) {
        const data = notification.data || {}
        const rowId = data.row_id
        const senderId = notification.sender ? notification.sender.id : null
        const signature = rowId
          ? `${notification.type}:${data.table_id || ''}:${rowId}:${senderId}`
          : null
        const last = groups[groups.length - 1]
        if (signature && last && last.signature === signature) {
          last.rest.push(notification)
          last.unread = last.unread || !notification.read
          continue
        }
        groups.push({
          key: `${notification.id}`,
          signature,
          first: notification,
          rest: [],
          unread: !notification.read,
        })
      }
      return groups
    },
  },
  watch: {
    loaded(isLoaded) {
      // On receiving many notifications, only the unread count is sent via web
      // sockets and the 'loaded' state resets to false in the store. This
      // watcher ensure to do the correct action if the panel is open and we
      // receive new notifications.

      if (isLoaded || !this.open) {
        return
      }

      // If we have no notifications, we can safely load the initial set.
      // Otherwise, we show a hint that new notifications are available.
      if (this.totalCount === 0) {
        this.initialLoad()
      } else {
        this.needRefresh = true
      }
    },
  },
  methods: {
    getTeleportedElement() {
      return getTeleportedElementFromRef(this.$refs, 'panelEl')
    },
    async initialLoad() {
      this.needRefresh = false
      try {
        await this.$store.dispatch('notification/fetchAll', {
          workspaceId: this.workspaceId,
        })
      } catch (e) {
        notifyIf(e, 'application')
      }
    },
    async updateShowEnablePush() {
      const webPush = this.$webPush
      if (!webPush || !webPush.isSupported) {
        this.showEnablePush = false
        return
      }
      // Mostra o convite quando a permissão ainda não foi decidida, ou foi
      // concedida mas este navegador ainda não está inscrito.
      if (webPush.permission === 'denied') {
        this.showEnablePush = false
      } else {
        this.showEnablePush = !(await webPush.isEnabled())
      }
    },
    async enablePush() {
      try {
        const enabled = await this.$webPush.enable()
        if (enabled) {
          this.showEnablePush = false
        }
      } catch (e) {
        notifyIf(e, 'application')
      }
    },
    show(target) {
      if (!this.loaded && !this.loading) {
        this.initialLoad()
      }
      this.updateShowEnablePush()
      this.open = true
      const opener = target
      this.removeOnClickOutsideHandler = onClickOutside(
        this.getTeleportedElement(),
        () => {
          if (this.open) {
            this.hide()
          }
        },
        {
          ignoreElements: () => {
            const childRoots = this.children.flatMap((child) =>
              collectTeleportRootsForOutsideClick(child)
            )
            return [opener, ...new Set(childRoots)].filter(Boolean)
          },
        }
      )
      this.$emit('shown')
    },
    registerChild(child) {
      this.children.push(child)
    },
    hide() {
      this.open = false
      this.opener = null
      if (this.removeOnClickOutsideHandler) {
        this.removeOnClickOutsideHandler()
        this.removeOnClickOutsideHandler = null
      }
      this.$emit('hidden')
    },
    toggle(target) {
      if (this.open) {
        this.hide()
      } else {
        this.show(target)
      }
    },
    async markAllAsRead() {
      try {
        await this.$store.dispatch('notification/markAllAsRead')
      } catch (error) {
        notifyIf(error, 'application')
      }
    },
    async clearAll() {
      try {
        await this.$store.dispatch('notification/clearAll')
      } catch (error) {
        notifyIf(error, 'application')
      }
    },
    getNotificationIcon(notification) {
      return this.$registry
        .get('notification', notification.type)
        .getIconComponent()
    },
    getNotificationIconProps(notification) {
      return this.$registry
        .get('notification', notification.type)
        .getIconComponentProps()
    },
    getNotificationContent(notification) {
      return this.$registry
        .get('notification', notification.type)
        .getContentComponent()
    },
    timeAgo(timestamp) {
      return moment.utc(timestamp).fromNow()
    },
    async loadNextPage() {
      try {
        await this.$store.dispatch('notification/fetchNextSetOfNotifications')
      } catch (e) {
        notifyIf(e, 'application')
      }
    },
  },
}
</script>
